import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Markdown from "@/components/Markdown";
import LeadCapture from "@/components/LeadCapture";
import { growth, formatError } from "@/lib/growthApi";

const INPUT = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none transition-colors focus:border-gold/60 placeholder:text-slate-600";
const LABEL = "mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500";
const GOALS = ["Win new customers", "Enter a new sector", "Enter a new geography", "Identify target companies", "Understand competitors", "Identify decision-makers", "Build BD pipeline", "Access additional BD support"];
const TIMEFRAMES = ["Immediate", "0–3 months", "3–6 months", "6–12 months", "Exploring options"];
const STEPS = [
  { title: "Company", fields: [["company_name", "Company name", "input"], ["website", "Company website", "input"]] },
  { title: "What You Do", fields: [["products_services", "What products or services do you provide?", "textarea"]] },
  { title: "Current Market", fields: [["current_sectors", "Which sectors do you currently work in?", "textarea"], ["current_regions", "Which countries or regions do you currently operate in?", "input"]] },
  { title: "Growth Goal", fields: [["growth_goals", "What are you trying to achieve?", "goals"]] },
  { title: "Ideal Customer", fields: [["ideal_customer", "Describe the type of company most likely to buy.", "textarea"]] },
  { title: "Main Challenge", fields: [["main_challenge", "What is currently preventing growth?", "textarea"]] },
  { title: "Timeframe", fields: [["timeframe", "When do you need to make progress?", "timeframe"]] },
];

export default function OpportunityBuilder() {
  const [step, setStep] = useState(0);
  const [v, setV] = useState({ company_name: "", website: "", products_services: "", current_sectors: "", current_regions: "", growth_goals: [], ideal_customer: "", main_challenge: "", timeframe: "Exploring options" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const cur = STEPS[step];
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const toggleGoal = (g) => setV({ ...v, growth_goals: v.growth_goals.includes(g) ? v.growth_goals.filter((x) => x !== g) : [...v.growth_goals, g] });
  const valid = cur.fields.every(([k, , type]) => (type === "goals" ? v.growth_goals.length > 0 : type === "timeframe" || k === "website" ? true : v[k].trim()));

  const next = async (e) => {
    e.preventDefault();
    if (!valid) return;
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try { setResult(await growth.builder(v)); } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="builder-page" className="pt-32 sm:pt-40 pb-24">
      <SEO title="Opportunity Builder | Thrive Business Development" description="A guided tool that produces a concise Commercial Growth Snapshot: current position, growth objective, potential routes, research questions and a suggested first step." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-[320px_1fr] lg:gap-20">
        <Reveal>
          <p className="eyebrow mb-5">Opportunity Builder</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-[-0.035em] leading-[1.05] text-white">A guided route to your Commercial Growth Snapshot.</h1>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed">Seven short steps. You will receive an initial view of your commercial position, potential growth routes, the questions worth researching and a sensible first step.</p>
          {!result && (
            <ol className="mt-10 space-y-3" data-testid="builder-steps">
              {STEPS.map((s, i) => (
                <li key={s.title} className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] ${i === step ? "text-gold" : i < step ? "text-slate-300" : "text-slate-600"}`}>
                  <span>0{i + 1}</span><span>{s.title}</span>
                </li>
              ))}
            </ol>
          )}
          <Link to="/growth/adviser" data-testid="builder-adviser-link" className="group mt-10 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold">Prefer a conversation? Open the Growth Adviser <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></Link>
        </Reveal>

        <div>
          {result ? (
            <div data-testid="builder-snapshot">
              <p className="eyebrow mb-4">Commercial Growth Snapshot</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">{v.company_name}</h2>
              <div className="mt-8 border border-white/10 bg-navy-light/30 p-6 sm:p-8 text-sm text-slate-300"><Markdown text={result.snapshot} /></div>
              {!showLead ? (
                <div className="mt-8 flex flex-wrap gap-3" data-testid="builder-actions">
                  <button type="button" onClick={() => setShowLead(true)} data-testid="builder-closer-look-button" className="group inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow">Would you like Thrive to take a closer look? <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></button>
                  <button type="button" onClick={() => { setResult(null); setStep(0); }} data-testid="builder-restart-button" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-gold hover:text-gold">Start again</button>
                </div>
              ) : (
                <div className="mt-8"><LeadCapture sessionId={result.session_id} defaults={{ company: v.company_name, website: v.website, commercial_challenge: v.main_challenge }} /></div>
              )}
            </div>
          ) : (
            <form onSubmit={next} className="border border-white/10 bg-navy-light/30 p-6 sm:p-10" data-testid="builder-form">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Step 0{step + 1} of 0{STEPS.length}</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-[-0.03em] text-white">{cur.title}</h2>
              <div className="mt-8 space-y-6">
                {cur.fields.map(([k, label, type]) => (
                  <div key={k}>
                    <label htmlFor={`b-${k}`} className={LABEL}>{label}</label>
                    {type === "input" && <input id={`b-${k}`} data-testid={`builder-${k}-input`} value={v[k]} onChange={set(k)} className={INPUT} autoFocus />}
                    {type === "textarea" && <textarea id={`b-${k}`} data-testid={`builder-${k}-input`} rows={4} value={v[k]} onChange={set(k)} className={INPUT} autoFocus />}
                    {type === "goals" && (
                      <div className="grid gap-2 sm:grid-cols-2" data-testid="builder-goals">
                        {GOALS.map((g) => (
                          <button key={g} type="button" onClick={() => toggleGoal(g)} data-testid={`builder-goal-${g.toLowerCase().replace(/\s+/g, "-")}`} className={`border px-4 py-3 text-left text-sm transition-colors ${v.growth_goals.includes(g) ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-slate-300 hover:border-gold/60"}`}>{g}</button>
                        ))}
                      </div>
                    )}
                    {type === "timeframe" && (
                      <div className="flex flex-wrap gap-2" data-testid="builder-timeframes">
                        {TIMEFRAMES.map((t) => (
                          <button key={t} type="button" onClick={() => setV({ ...v, timeframe: t })} data-testid={`builder-timeframe-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${v.timeframe === t ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-slate-400 hover:border-gold/60"}`}>{t}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-10 flex items-center justify-between gap-4">
                <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="builder-back-button" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-gold disabled:opacity-30">Back</button>
                <button type="submit" disabled={!valid || busy} data-testid="builder-next-button" className="group inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  {step === STEPS.length - 1 ? (busy ? "Preparing snapshot" : "Create my snapshot") : "Continue"}
                  {!busy && <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
